import * as React from 'react';
import {
    FileText,
    Trash2,
    ExternalLink,
    MessageSquare,
    Send,
    Loader2,
    Edit2,
    X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
    useDeleteDeliverableCommentMutation,
    useUpdateDeliverableCommentMutation,
    useGetDeliverableCommentsQuery,
    useAddDeliverableCommentMutation,
    useGetExternalDeliverableCommentsQuery,
    useAddExternalDeliverableCommentMutation,
    useDeleteExternalDeliverableCommentMutation,
    useUpdateExternalDeliverableCommentMutation,
} from '@/state';
import { useGetCurrentUserQuery } from '@/state/user/user-api';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { cn } from '@/lib/utils';
import { toLexicalJSON, fromLexicalJSON } from '@/lib/lexical-utils';
import type { DeliverableComment, DeliverableItemProps } from '@/types';

/**
 * Helper function to generate avatar color based on user name
 */
function getAvatarBgColor(firstName: string): string {
    const colors = [
        'bg-blue-100 text-blue-700',
        'bg-emerald-100 text-emerald-700',
        'bg-amber-100 text-amber-700',
        'bg-rose-100 text-rose-700',
        'bg-violet-100 text-violet-700',
        'bg-cyan-100 text-cyan-700',
    ];
    const charCode = (firstName?.[0] || 'U').charCodeAt(0);
    return colors[charCode % colors.length];
}

/**
 * Helper function to format relative time
 */
function formatRelativeTime(date: string): string {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return d.toLocaleDateString();
}

export function DeliverableItem({
    doc,
    tenant,
    isExpanded,
    onToggle,
    onDelete,
    isExternal,
    isReadOnly = false,
}: DeliverableItemProps) {
    // ============================================================================
    // STATE MANAGEMENT & API HOOKS
    // ============================================================================

    // API (Internal)
    const { data: internalComments = [], refetch: refetchInternalComments } = useGetDeliverableCommentsQuery(
        { tenant, deliverableId: doc.id },
        { skip: !isExpanded || isExternal }
    );
    const [addInternalComment] = useAddDeliverableCommentMutation();
    const [deleteInternalComment] = useDeleteDeliverableCommentMutation();
    const [updateInternalComment] = useUpdateDeliverableCommentMutation();

    // API (External)
    const { data: externalComments = [], refetch: refetchExternalComments } = useGetExternalDeliverableCommentsQuery(
        { deliverableId: doc.id },
        { skip: !isExpanded || !isExternal }
    );
    const [addExternalComment] = useAddExternalDeliverableCommentMutation();
    const [deleteExternalComment] = useDeleteExternalDeliverableCommentMutation();
    const [updateExternalComment] = useUpdateExternalDeliverableCommentMutation();

    const comments = isExternal ? externalComments : internalComments;
    const refetchComments = isExternal ? refetchExternalComments : refetchInternalComments;

    const { data: currentUser } = useGetCurrentUserQuery(
        { tenant: tenant || 'public' },
        { skip: !tenant && !isExternal }
    );

    const [commentText, setCommentText] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [editingCommentId, setEditingCommentId] = React.useState<number | null>(null);
    const [editingText, setEditingText] = React.useState('');
    const [isUpdating, setIsUpdating] = React.useState(false);

    // ============================================================================
    // DERIVED VALUES
    // ============================================================================

    const fileExt = doc.attachment?.split('.').pop()?.toUpperCase() || 'FILE';

    // ============================================================================
    // HANDLERS
    // ============================================================================

    const handleDeleteComment = async (id: number) => {
        try {
            if (isExternal) {
                await deleteExternalComment({ commentId: id, deliverableId: doc.id }).unwrap();
            } else {
                await deleteInternalComment({ tenant, commentId: id }).unwrap();
            }
            toast.success('Comment deleted');
            refetchComments();
        } catch {
            toast.error('Failed to delete comment');
        }
    };

    const startEditing = (id: number, text: string) => {
        setEditingCommentId(id);
        setEditingText(fromLexicalJSON(text));
    };

    const handleUpdateComment = async () => {
        if (!editingCommentId || !editingText.trim()) return;
        try {
            setIsUpdating(true);
            const payload = toLexicalJSON(editingText);
            
            if (isExternal) {
                await updateExternalComment({
                    commentId: editingCommentId,
                    deliverableId: doc.id,
                    data: { comments: payload, deliverables: doc.id },
                }).unwrap();
            } else {
                await updateInternalComment({
                    tenant,
                    commentId: editingCommentId,
                    comment: payload,
                }).unwrap();
            }

            setEditingCommentId(null);
            setEditingText('');
            toast.success('Comment updated');
            refetchComments();
        } catch {
            toast.error('Failed to update comment');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleAddComment = async () => {
        if (!commentText.trim()) return;
        try {
            setIsSubmitting(true);
            const payload = toLexicalJSON(commentText);

            if (isExternal) {
                await addExternalComment({
                    deliverableId: doc.id,
                    data: { comments: payload, deliverables: doc.id },
                }).unwrap();
            } else {
                await addInternalComment({
                    tenant,
                    deliverableId: doc.id,
                    resourceId: doc.id,
                    comment: payload,
                }).unwrap();
            }

            setCommentText('');
            toast.success('Comment added');
            refetchComments();
        } catch {
            toast.error('Failed to add comment');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ============================================================================
    // RENDER
    // ============================================================================

    return (
        <div
            className={cn(
                'relative transition-all duration-300 overflow-hidden',
                isExpanded
                    ? 'border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg bg-white dark:bg-slate-900'
                    : 'border-b border-slate-100 dark:border-slate-800 pb-4 mb-4'
            )}
        >
            {/* Header - Deliverable Info */}
            <div className={cn(
                'p-4 md:p-6 cursor-pointer transition-all',
                isExpanded && 'border-b border-slate-100 dark:border-slate-800'
            )}
            onClick={onToggle}
            >
                <div className="flex items-start justify-between gap-4">
                    {/* Document Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-200 to-slate-100 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center flex-shrink-0">
                                <FileText className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-slate-900 dark:text-slate-50 text-base md:text-lg truncate">
                                    {doc.title}
                                </h3>
                                <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">
                                    {doc.created_by?.first_name || 'System'} • {new Date(doc.created_at).toLocaleDateString()}
                                </p>
                            </div>
                        </div>

                        {/* File Type Badge */}
                        <div className="flex gap-2 items-center flex-wrap">
                            <Badge variant="secondary" className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                {fileExt}
                            </Badge>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                                {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
                            </span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
                            asChild
                            onClick={(e) => e.stopPropagation()}
                        >
                            <a href={doc.attachment} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                                'h-9 w-9 transition-all',
                                isExpanded
                                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-50'
                                    : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggle();
                            }}
                        >
                            <MessageSquare className="w-4 h-4" />
                        </Button>

                        {!isReadOnly && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="p-4 md:p-6 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 lg:gap-8">
                     

                        {/* RIGHT - Comments */}
                        <div className="space-y-4 order-1 lg:order-1 flex flex-col">
                            <div className="flex items-center justify-between">
                                <div className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                    Comments & Activity
                                </div>
                                <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                                    {comments.length}
                                </span>
                            </div>

                            {/* Comments Container */}
                            <div className="flex-1 space-y-3 overflow-y-auto custom-scrollbar max-h-96 lg:max-h-[500px] pr-2">
                                {comments.length > 0 ? (
                                    comments.map((c: DeliverableComment) => (
                                        <div
                                            key={c.id}
                                            className="group bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-100 dark:border-slate-700 transition-all hover:border-slate-200 dark:hover:border-slate-600"
                                        >
                                            {/* Comment Header */}
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <div className="flex items-center gap-2 flex-1">
                                                    <div className={cn(
                                                        'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0',
                                                        getAvatarBgColor(c.created_by?.first_name || 'U')
                                                    )}>
                                                        {(c.created_by?.first_name?.[0] || 'U').toUpperCase()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                                                            {c.created_by?.first_name} {c.created_by?.last_name}
                                                        </p>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                                            {formatRelativeTime(c.created_on ?? "")}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Comment Actions */}
                                                {!isReadOnly && currentUser?.id === c.created_by?.id && (
                                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                                        <button
                                                            onClick={() => startEditing(c.id, c.comments)}
                                                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                                        >
                                                            <Edit2 className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteComment(c.id)}
                                                            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Comment Body */}
                                            {editingCommentId === c.id ? (
                                                <div className="space-y-2 mt-3">
                                                    <textarea
                                                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-500 resize-none"
                                                        value={editingText}
                                                        onChange={(e) => setEditingText(e.target.value)}
                                                        autoFocus
                                                        rows={3}
                                                    />
                                                    <div className="flex gap-2 justify-end">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setEditingCommentId(null)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            onClick={handleUpdateComment}
                                                            disabled={isUpdating}
                                                        >
                                                            {isUpdating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                                            Save
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed mt-2">
                                                    <ReadOnlyContentDisplay html={c.comments} className='h-fit' />
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-center opacity-60">
                                        <MessageSquare className="w-8 h-8 text-slate-400 dark:text-slate-600 mb-2" />
                                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                            No comments yet
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                                            Be the first to comment
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Comment Input */}
                            {!isReadOnly && (
                                <div className="border-t border-slate-100 dark:border-slate-700 pt-4 mt-4">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="Add a comment..."
                                            value={commentText}
                                            onChange={(e) => setCommentText(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                                            className="flex-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-500 transition-all"
                                        />
                                        <Button
                                            size="icon"
                                            onClick={handleAddComment}
                                            disabled={!commentText.trim() || isSubmitting}
                                            className="h-10 w-10"
                                        >
                                            {isSubmitting ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Send className="w-4 h-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
